import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { formatAmount } from "../../lib/finance";
import { ClaimBadge, ClaimSheet } from "./ClaimsPanel";
import { AdvanceDialog, AdvanceLine } from "./AdvancesPanel";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Banknote, Plus, Receipt } from "lucide-react";

// « Frais » card of a mission: one's own notes de frais on this mission and how to add one; an organiser can declare for a
// colleague without account of the team; an approver sees what waits for approval here and hands over mission advances.
// The server checks everything again.
export default function MissionClaimsCard({ mission, canClaim, canApprove }) {
  const [mine, setMine] = useState(null);
  const [pending, setPending] = useState(null);
  const [advances, setAdvances] = useState([]);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    const params = { mission_id: mission.id };
    if (canClaim) api.get("/finance/claims/mine", { params }).then(({ data }) => setMine(data.items)).catch(() => setMine([]));
    if (canApprove) {
      api.get("/finance/claims", { params }).then(({ data }) => setPending(data.items)).catch(() => setPending([]));
      api.get("/finance/advances", { params }).then(({ data }) => setAdvances(data.items)).catch(() => setAdvances([]));
    }
  }, [mission.id, canClaim, canApprove]);
  useEffect(() => { load(); }, [load]);

  const fichesWithoutAccount = mission.capabilities.manage_team ? mission.team.filter((a) => a.member.kind === "collaborator") : [];
  const back = `/missions/${mission.id}`;
  const rows = [...(mine || []), ...(pending || []).filter((p) => !(mine || []).some((m) => m.id === p.id))];
  const loading = (canClaim && mine === null) || (canApprove && pending === null);

  return (
    <Card className="p-5 space-y-3" data-testid="mission-claims">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Receipt size={16} className="text-slate-400" />Frais</h3>
        {canClaim && (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/finance/frais/nouvelle?mission=${mission.id}`} state={{ back }} data-testid="mission-claim-add"><Plus size={14} className="mr-1.5" />Ajouter un frais</Link>
          </Button>
        )}
      </div>
      {loading ? <Skeleton className="h-12 w-full" /> : rows.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune note de frais sur cette mission.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => setOpenId(c.id)} className="w-full text-left flex items-center gap-3 py-2 hover:bg-slate-50 -mx-2 px-2 rounded-md"
                data-testid={`mission-claim-${c.id}`}>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{c.title} <span className="text-slate-500">· {c.claimant.name}</span></span>
                <span className="text-sm font-medium text-slate-900">{formatAmount(c.amount, c.currency)}</span>
                <ClaimBadge claim={c} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {canClaim && fichesWithoutAccount.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
          {fichesWithoutAccount.map((a) => (
            <Button key={a.id} variant="ghost" size="sm" asChild>
              <Link to={`/finance/frais/nouvelle?mission=${mission.id}&pour=${a.member.id}`} state={{ back, claimantName: a.member.name }}
                data-testid={`mission-claim-for-${a.member.id}`}>
                <Plus size={13} className="mr-1" />Frais de {a.member.name}
              </Link>
            </Button>
          ))}
        </div>
      )}
      {canApprove && (
        <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="mission-advances">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-slate-500">Avances de mission</span>
            <Button variant="ghost" size="sm" onClick={() => setAdvanceOpen(true)} data-testid="mission-advance-add"><Banknote size={14} className="mr-1.5" />Remettre une avance</Button>
          </div>
          {advances.length > 0 ? <ul className="divide-y divide-slate-100 -mx-4">{advances.map((a) => <AdvanceLine key={a.id} a={a} />)}</ul>
            : <p className="text-sm text-slate-500">Aucune avance remise pour cette mission.</p>}
        </div>
      )}
      <AdvanceDialog open={advanceOpen} onOpenChange={setAdvanceOpen} onRecorded={load} mission={mission} />
      <ClaimSheet claimId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </Card>
  );
}
